'use client';

import Icon from '@/components/ui/AppIcon';

export default function ComparisonTable() {
  const comparisons = [
    {
      category: 'Gaspreis (Ø, Business)',
      germany: '0,12 € / kWh (ca. 20% teurer)',
      poland: '0,10 € / kWh',
      icon: 'LightBulbIcon' as const,
    },
    {
      category: 'Strompreis (Ø, Business)',
      germany: '0,20 € / kWh (ca. 54% teurer)',
      poland: '0,13 € / kWh',
      icon: 'LightBulbIcon' as const,
    },
    {
      category: 'Miete Hauptstadt €/m² (Ø)',
      germany: 'Berlin: ca. 37 € (ca. 58% teurer)',
      poland: 'Warschau: ca. 24 €',
      icon: 'BuildingOfficeIcon' as const,
    },
    {
      category: 'Körperschaftsteuer (effektiv)',
      germany: '29% - 33% (je nach Gemeinde)',
      poland: '5% - 9% - 19% (9% bei Umsatz unter 2 Mio. €, 5% IP-Box für Software/IP)',
      icon: 'CurrencyDollarIcon' as const,
    },
    {
      category: 'Netto vom Brutto (Ø Arbeitnehmer)',
      germany: '52,10%',
      poland: '65,30%',
      icon: 'CurrencyDollarIcon' as const,
    },
    {
      category: 'Durchschnittl. Arbeitskosten pro Stunde',
      germany: '43,40 € / Stunde',
      poland: '17,30 € / Stunde',
      icon: 'CurrencyDollarIcon' as const,
    },
    {
      category: 'Mindestlohn',
      germany: '2.161 €',
      poland: '1.091 €',
      icon: 'CurrencyDollarIcon' as const,
    },
    {
      category: 'Regulierung & Verwaltung',
      germany: 'Sehr hohe Regelungsdichte, langsame Verfahren, hohe Dokumentationspflichten',
      poland: 'Weniger komplexe Pflichten, schnelle Umsetzung, klarere Kostenstruktur',
      icon: 'DocumentTextIcon' as const,
    },
    {
      category: 'Arbeitsmarkt',
      germany: 'Alternder Arbeitsmarkt, Fachkräftemangel',
      poland: 'Großer, gut ausgebildeter Arbeitsmarkt mit hoher Verfügbarkeit qualifizierter Fachkräfte',
      icon: 'GlobeAltIcon' as const,
    },
    {
      category: 'Digitalisierung & Behördenprozesse',
      germany: 'Fragmentierte Digitalisierung, oft physische Anwesenheit erforderlich',
      poland: 'Weitgehend digitalisierte Verwaltung, Unternehmensgründung vollständig online möglich',
      icon: 'DocumentTextIcon' as const,
    },
    {
      category: 'Erstaufenthaltstitel für Arbeitskräfte (2024)',
      germany: '77.149 Personen',
      poland: '337.874 Personen (ca. 338% höher)',
      icon: 'GlobeAltIcon' as const,
    },
    {
      category: 'EU-Investitionen pro Jahr',
      germany: 'Ca. 14 Mrd. €',
      poland: 'Ca. 25 Mrd. € (ca. 78% höher)',
      icon: 'CurrencyDollarIcon' as const,
    },
    {
      category: 'Auswanderung außerhalb der EU (2023)',
      germany: '574.200 Personen (ca. 195% mehr Abwanderung)',
      poland: '194.100 Personen',
      icon: 'GlobeAltIcon' as const,
    },
    {
      category: 'Wirtschaftswachstum 2015-2024',
      germany: '27,5% Wachstum',
      poland: '90,7% Wachstum',
      icon: 'ChartBarIcon' as const,
    },
  ];

  return (
    <section className="py-6 px-6 md:px-12 max-w-8xl mx-auto bg-muted/30">
      <div className="text-center mb-4">
        <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-2">
          Vergleich Deutschland vs. Polen
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Ein detaillierter Überblick über die wichtigsten Unterschiede zwischen beiden Standorten
        </p>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full bg-card border-2 border-primary/20 rounded-2xl overflow-hidden shadow-lg">
            <thead className="bg-primary text-primary-foreground">
              <tr>
                <th className="px-6 py-4 text-left font-semibold">Kategorie</th>
                <th className="px-6 py-4 text-left font-semibold">Deutschland</th>
                <th className="px-6 py-4 text-left font-semibold">Polen</th>
              </tr>
            </thead>
            <tbody>
              {comparisons.map((item, index) => (
                <tr
                  key={`comparison_${index}`}
                  className="border-t border-primary/10 hover:bg-primary/5 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Icon name={item.icon} size={20} className="text-primary flex-shrink-0" />
                      <span className="font-medium text-foreground">{item.category}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{item.germany}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{item.poland}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {comparisons.map((item, index) => (
            <div key={`comparison_mobile_${index}`} className="bg-card border-2 border-primary/20 rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <Icon name={item.icon} size={24} className="text-primary flex-shrink-0" />
                <h3 className="font-semibold text-foreground">{item.category}</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Deutschland
                  </div>
                  <div className="text-sm text-foreground">{item.germany}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Polen
                  </div>
                  <div className="text-sm text-foreground">{item.poland}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}